import './dashboard.scss';

import { format } from 'date-fns';
import * as React from 'react';
import { connect, DispatchProp } from 'react-redux';
import { withRouter } from 'react-router';
import queryString from 'query-string';

import { removeFilterData } from '../../actions/dashboardAction';
import { IDailyForms, IFilters, IFlaggedFormDetails, IHistory, IMonthlyForms, IQuarterlyForms } from '../../interfaces';
import { getFlaggedForms, getFormSummaryData, getIndividualFormSummary } from '../../services/dashboardService';
import { splitDate } from '../../utils/generalUtils';
import { Async } from '../reusableComponents/Async';
import { BasePage } from '../reusableComponents/BasePage';
import { ErrorPage } from '../reusableComponents/ErrorPage';
import { Loader } from '../reusableComponents/Loader';
import { DashboardData } from './DashboardData';

export interface IDashboardProps extends DispatchProp<any> {
    dailyForms: IDailyForms;
    monthlyForms: IMonthlyForms;
    quarterlyForms: IQuarterlyForms;
    lineChartData: {
        dailyForms: IDailyForms;
        Monthly: IMonthlyForms;
        Quarterly: IQuarterlyForms;
    };
    history: IHistory;
    dailyFlaggedForms: IFlaggedFormDetails[];
    monthlyFlaggedForms: IFlaggedFormDetails[];
    quarterlyFlaggedForms: IFlaggedFormDetails[];
    dashboard: Object;
    monthlyFilter: IFilters;
    dailyFilter: IFilters;
    quarterlyFilter: IFilters;
    lineChartFilter: IFilters;
    resetFilters: boolean;
    dailyFlaggedFormCount: string;
    monthlyFlaggedFormCount: string;
    quarterlyFlaggedFormCount: string;
    location: any;
}

export interface IDashboardState {
    activeIndex?: number;
    lineChartData?: Object[];
    dashboardFilterValue: string;
    dailyFilter: string;
    monthlyFilter: string;
    quarterlyFilter: string;
    filterMonthlyBy?: string;
    filterQuarterlyBy?: string;
    barColors?: string[];
    lineChartDataKey?: string[];
    region: string;
}

export class DashboardImpl extends React.Component<IDashboardProps, IDashboardState> {
    constructor(props: IDashboardProps) {
        super(props);
        let region = 'All';
        let dashboardFilterValue = format(new Date(), 'YYYY-MM-DD');
        let dailyFilter = format(new Date(), 'YYYY-MM-DD');
        let monthlyFilter = format(new Date(), 'YYYY-MM-DD');
        let quarterlyFilter = format(new Date(), 'YYYY-MM-DD');
        if (this.props.location.search) {
            dashboardFilterValue = queryString.parse(this.props.location.search).date;
            region = queryString.parse(this.props.location.search).region;
            dailyFilter = (queryString.parse(this.props.location.search).daily);
            monthlyFilter = (queryString.parse(this.props.location.search).monthly);
            quarterlyFilter = (queryString.parse(this.props.location.search).quarterly);
        }
        this.state = {
            dashboardFilterValue, dailyFilter, monthlyFilter, quarterlyFilter, region
        };
    }

    static defaultProps: IDashboardProps;

    identifier = 'Dashboard';

    promise = async () => {
        const { dailyFilter, monthlyFilter, quarterlyFilter, region, dashboardFilterValue } = this.state;
        const { dd: dailyDD, mm: dailyMM, yyyy: dailyYYYY } = splitDate(dailyFilter);
        const { dd: monthlyDD, mm: monthlyMM, yyyy: monthlyYYYY } = splitDate(monthlyFilter);
        const { dd: quarterlyDD, mm: quarterlyMM, yyyy: quarterlyYYYY } = splitDate(quarterlyFilter);
        removeFilterData();
        if (!this.props.location.search) { 
            await getFlaggedForms('init', dailyMM, dailyDD, dailyYYYY, region, this);
            await getIndividualFormSummary(dailyMM, dailyDD, dailyYYYY, 'Daily', region, 'init');
            return;
        }
        await getFlaggedForms('dailyForm', dailyMM, dailyDD, dailyYYYY, region, this);
        await getFlaggedForms('monthlyForm', monthlyMM, monthlyDD, monthlyYYYY, region, this);
        await getFlaggedForms('ShopInspection', quarterlyMM, quarterlyDD, quarterlyYYYY, region, this);
        await getIndividualFormSummary(dailyMM, dailyDD, dailyYYYY, 'Daily', region);
        await getIndividualFormSummary(monthlyMM, monthlyDD, monthlyYYYY, 'Monthly', region);
        await getIndividualFormSummary(quarterlyMM, quarterlyDD, quarterlyYYYY, 'Quarterly', region);
    }

    renderContent = () => {
        const { dailyForms, history, monthlyForms, lineChartData, quarterlyForms, dailyFlaggedForms,
            monthlyFlaggedForms, dailyFilter, quarterlyFlaggedForms, resetFilters,
            monthlyFilter, quarterlyFilter, lineChartFilter } = this.props;
        if (!dailyForms || !monthlyForms || !quarterlyForms || !lineChartData) {
            return <div></div>;
        }
        const filterData = {
            dailyFilter, monthlyFilter, quarterlyFilter, lineChartFilter
        };
        return (
            < DashboardData
                dailyFlaggedFormCount={this.props.dailyFlaggedFormCount}
                monthlyFlaggedFormCount={this.props.monthlyFlaggedFormCount}
                quarterlyFlaggedFormCount={this.props.quarterlyFlaggedFormCount}
                dailyForms={dailyForms}
                monthlyForms={monthlyForms}
                lineChartData={lineChartData}
                quarterlyForms={quarterlyForms}
                history={history}
                dailyFlaggedForms={dailyFlaggedForms}
                monthlyFlaggedForms={monthlyFlaggedForms}
                quarterlyFlaggedForms={quarterlyFlaggedForms}
                filterData={filterData}
                location={this.props.location}
                resetFilters={resetFilters}
            />
        );
    }

    render() {
        return (
            <BasePage pageTitle="Argos Dashboard">
                <Async
                    identifier={this.identifier}
                    promise={this.promise}
                    loader={<Loader type="async" />}
                    error={<ErrorPage />}
                    content={this.renderContent()}
                />
            </BasePage>
        );
    }
}

export function mapStateToProps(state) {
    return {
        dailyForms: state.dashboard.get('dailyFormSummary'),
        monthlyForms: state.dashboard.get('monthlyFormSummary'),
        quarterlyForms: state.dashboard.get('quarterlyFormSummary'),
        lineChartData: state.dashboard.get('lineChartData'),
        plantDetails: state.dashboard.get('plantDetails'),
        dailyFlaggedForms: state.dashboard.get('flaggedDailyForms'),
        monthlyFlaggedForms: state.dashboard.get('flaggedMonthlyForms'),
        quarterlyFlaggedForms: state.dashboard.get('flaggedQuarterlyForms'),
        dashboard: state.dashboard.toJS(),
        dailyFlaggedFormCount: state.dashboard.get('dailyCount'),
        monthlyFlaggedFormCount: state.dashboard.get('monthlyCount'),
        quarterlyFlaggedFormCount: state.dashboard.get('quarterlyCount'),
        monthlyFilter: state.dashboard.get('monthlyFilter'),
        dailyFilter: state.dashboard.get('dailyFilter'),
        quarterlyFilter: state.dashboard.get('quarterlyFilter'),
        lineChartFilter: state.dashboard.get('lineChartFilter'),
        resetFilters: state.dashboard.get('resetFilters'),
    };
}

export const Dashboard = withRouter(connect<{}, {}, IDashboardProps>(mapStateToProps)(DashboardImpl));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Dashboard/index.tsx